<div class="page-head">
  <div class="page-head-main">
    <h1 class="page-title">
      @if (portfolio?.name) {
        <span class="pf-title-name">{{ portfolio.name }}</span>
      }
      @if (portfolio?.name) {
        <span class="pf-title-sep">·</span>
      }
      Backtest
      @if (portfolio) {
        <span
          class="pill"
          [class.pill-ready]="!isApplying"
          [class.pill-computing]="isApplying"
        >
          @if (!isApplying) {
            {{ portfolio.start_date }} → {{ portfolio.end_date }}
          }
          @if (isApplying) {
            Recalculating…
          }
        </span>
      }
    </h1>
    <div class="page-sub">
      Benchmark:
      {{ benchmark?.index_name_short || portfolio?.benchmark || "—" }} ·
      {{ constituents.length }} assets
    </div>

    @if (portfolio) {
      <div class="exp-settings-strip">
        <div class="exp-field">
          <label>Start date</label>
          <input
            type="date"
            class="exp-input"
            [min]="portfolio.start_date"
            [(ngModel)]="editStart"
            [disabled]="isApplying"
          />
        </div>
        <div class="exp-field">
          <label>End date</label>
          <input
            type="date"
            class="exp-input"
            [min]="editStart || portfolio.start_date"
            [(ngModel)]="editEnd"
            [disabled]="isApplying"
          />
        </div>
        <div class="exp-field exp-field-grow">
          <label>Benchmark</label>
          <select
            class="exp-input exp-select"
            [(ngModel)]="editBenchmarkName"
            [disabled]="isApplying"
          >
            <option value="" disabled>Select benchmark</option>
            @for (g of benchmarkGroups; track g) {
              <optgroup [label]="g.label">
                @for (b of g.items; track b) {
                  <option [value]="b.name">{{ b.full }}</option>
                }
              </optgroup>
            }
            @if (editBenchmarkName && !benchmarkGroups.length) {
              <option [value]="editBenchmarkName">
                {{ editBenchmarkName }}
              </option>
            }
          </select>
        </div>
        <div class="exp-settings-actions">
          <button
            class="btn btn-text btn-sm"
            type="button"
            (click)="onResetSettings()"
            [disabled]="isApplying || !hasSettingsChanges"
          >
            Reset
          </button>
          <button
            class="btn btn-primary btn-sm"
            type="button"
            (click)="onApply()"
            [disabled]="!canApply"
          >
            @if (!isApplying) {
              Apply
            }
            @if (isApplying) {
              Applying…
            }
          </button>
        </div>
      </div>
    }
  </div>
  <div class="row" style="gap: 10px">
    <button
      class="btn btn-ghost"
      type="button"
      (click)="onExportPdf()"
      [disabled]="!portfolio || isApplying"
    >
      Export
    </button>
  </div>
</div>

@if (!isLoading) {
  <div class="bento">
    <!-- Row 1: KPI tiles -->
    <section class="kpi exp-col-3">
      <span class="label">Total return</span>
      <span
        class="value"
        [class.pos]="(kpis.totalReturn ?? 0) > 0"
        [class.neg]="(kpis.totalReturn ?? 0) < 0"
      >
        {{ fmtDeltaPct(kpis.totalReturn, 2) }}
      </span>
      <span
        class="delta"
        [class.pos]="(kpis.totalReturn ?? 0) > (benchKpis.totalReturn ?? 0)"
        [class.neg]="(kpis.totalReturn ?? 0) <= (benchKpis.totalReturn ?? 0)"
      >
        @if (benchKpis.totalReturn !== null) {
          {{
            (kpis.totalReturn ?? 0) > (benchKpis.totalReturn ?? 0) ? "▲" : "▼"
          }}
          vs {{ benchmark?.index_name_short || "Bench" }}
          {{ fmtDeltaPct(benchKpis.totalReturn, 1) }}
        }
        @if (benchKpis.totalReturn === null) {
          Since inception
        }
      </span>
      <app-sparkline
        class="spark"
        [data]="sparks.total"
        [width]="90"
        [height]="24"
      ></app-sparkline>
    </section>
    <section class="kpi exp-col-3">
      <span class="label">Annualized return</span>
      <span class="value">{{ fmtPct(kpis.annualizedReturn, 2) }}</span>
      <span class="delta">CAGR 5Yr: {{ fmtPct(kpis.cagrY5, 2) }}</span>
      <app-sparkline
        class="spark"
        [data]="sparks.annual"
        [width]="90"
        [height]="24"
      ></app-sparkline>
    </section>
    <section class="kpi exp-col-3">
      <span class="label">Sharpe ratio</span>
      <span class="value">{{ fmtNum(kpis.sharpe) }}</span>
      <span
        class="delta"
        [class.pos]="(kpis.sharpe ?? 0) > (benchKpis.sharpe ?? 0)"
        [class.neg]="(kpis.sharpe ?? 0) <= (benchKpis.sharpe ?? 0)"
      >
        @if (benchKpis.sharpe !== null) {
          {{ (kpis.sharpe ?? 0) > (benchKpis.sharpe ?? 0) ? "▲" : "▼" }}
          vs {{ benchmark?.index_name_short || "Bench" }}
          {{ fmtNum(benchKpis.sharpe) }}
        }
        @if (benchKpis.sharpe === null) {
          Risk-adjusted
        }
      </span>
      <app-sparkline
        class="spark"
        [data]="sparks.sharpe"
        [width]="90"
        [height]="24"
        color="#00E5A0"
      ></app-sparkline>
    </section>
    <section class="kpi exp-col-3">
      <span class="label">Max drawdown</span>
      <span class="value neg">{{ fmtPct(kpis.maxDrawdown, 2) }}</span>
      <span class="delta neg">
        @if (benchKpis.maxDrawdown !== null) {
          ▼ vs {{ benchmark?.index_name_short || "Bench" }}
          {{ fmtPct(benchKpis.maxDrawdown, 1) }}
        }
        @if (benchKpis.maxDrawdown === null) {
          Peak-to-trough
        }
      </span>
      <app-sparkline
        class="spark"
        [data]="sparks.drawdown"
        [width]="90"
        [height]="24"
        color="#FF5C7A"
      ></app-sparkline>
    </section>
    <!-- Row 2: Cumulative chart + Valuation/Risk stack -->
    @if (plotData?.cumulative_returns?.Portfolio?.length) {
      <section class="card exp-col-8">
        <div class="card-head">
          <div class="card-title">
            Cumulative return
            <span class="hint"
              >· Portfolio vs
              {{ benchmark?.index_name_short || "Benchmark" }}</span
            >
          </div>
        </div>
        <app-exp-cumulative-return-chart
          [plotData]="plotData"
          [benchmark]="benchmark"
          [height]="380"
        ></app-exp-cumulative-return-chart>
      </section>
    }
    <div
      class="exp-col-4"
      style="display: flex; flex-direction: column; gap: 14px"
    >
      <div class="card">
        <div class="card-head"><div class="card-title">Valuation</div></div>
        <div class="kv">
          <span class="k">Dividend yield</span>
          <span class="v">{{
            valuation.dividendYield !== null
              ? fmtNum(valuation.dividendYield, 2) + "%"
              : "—"
          }}</span>
          <span class="k">P/E</span>
          <span class="v">{{ fmtNum(valuation.pe, 2) }}</span>
          <span class="k">P/S</span>
          <span class="v">{{ fmtNum(valuation.ps, 2) }}</span>
          <span class="k">P/B</span>
          <span class="v">{{ fmtNum(valuation.pb, 2) }}</span>
          <span class="k">Avg. Market Cap</span>
          <span class="v">{{ fmtMoney(valuation.avgMarketCap) }}</span>
          <span class="k">Avg. Enterprise Value</span>
          <span class="v">{{ fmtMoney(valuation.avgEnterpriseValue) }}</span>
          <span class="k">Avg. EBITDA</span>
          <span class="v">{{ fmtMoney(valuation.avgEBITDA) }}</span>
        </div>
      </div>
      <div class="card">
        <div class="card-head"><div class="card-title">Risk snapshot</div></div>
        <div class="kv">
          <span class="k">Volatility</span>
          <span class="v">{{ fmtPct(risk.volatility, 2) }}</span>
          <span class="k">VaR 95 / 99</span>
          <span class="v"
            >{{ fmtPct(risk.var95, 2) }} / {{ fmtPct(risk.var99, 2) }}</span
          >
          <span class="k">Alpha</span>
          <span
            class="v"
            [class.pos]="(risk.alpha ?? 0) >= 0"
            [class.neg]="(risk.alpha ?? 0) < 0"
            >{{ fmtPct(risk.alpha, 2) }}</span
          >
          <span class="k">Beta</span>
          <span class="v">{{ fmtNum(risk.beta) }}</span>
          <span class="k">Tracking error</span>
          <span class="v">{{ fmtPct(risk.trackingError, 2) }}</span>
          <span class="k">Information ratio</span>
          <span class="v">{{ fmtNum(risk.informationRatio) }}</span>
        </div>
      </div>
    </div>
    <!-- Row 3: Annual performance + Weights vs Volatility -->
    @if (hasAnnualData) {
      <section class="card exp-col-6">
        <div class="card-head">
          <div class="card-title">Annual performance</div>
          <div class="card-tools">
            <div class="seg">
              <button
                type="button"
                [class.active]="annualMetric === 'annualized_return'"
                (click)="setAnnualMetric('annualized_return')"
              >
                Return
              </button>
              <button
                type="button"
                [class.active]="annualMetric === 'annualized_sharpe'"
                (click)="setAnnualMetric('annualized_sharpe')"
              >
                Sharpe
              </button>
              <button
                type="button"
                [class.active]="annualMetric === 'annualized_std_deviation'"
                (click)="setAnnualMetric('annualized_std_deviation')"
              >
                Volatility
              </button>
            </div>
          </div>
        </div>
        <app-exp-annual-performance-chart
          [plotData]="plotData"
          [benchmark]="benchmark"
          [portfolio]="portfolio"
          [metric]="annualMetric"
          [height]="220"
        ></app-exp-annual-performance-chart>
      </section>
    }
    @if (weightsRows.length) {
      <section class="card exp-col-6">
        <div class="card-head">
          <div class="card-title">Weights vs Volatility per asset</div>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 18px">
          <div>
            <div
              style="
                font-size: 10.5px;
                color: var(--text-dim);
                text-transform: uppercase;
                letter-spacing: 0.05em;
                margin-bottom: 6px;
              "
            >
              Portfolio weight
            </div>
            <app-hbar-list
              [rows]="weightsRows"
              [format]="fmtPct.bind(this)"
            ></app-hbar-list>
          </div>
          <div>
            <div
              style="
                font-size: 10.5px;
                color: var(--text-dim);
                text-transform: uppercase;
                letter-spacing: 0.05em;
                margin-bottom: 6px;
              "
            >
              Annualized volatility
            </div>
            <app-hbar-list
              [rows]="volRows"
              [format]="fmtPct.bind(this)"
              colorMode="neg"
            ></app-hbar-list>
          </div>
        </div>
      </section>
    }
    <!-- Row 4: Constituents table -->
    @if (constituents.length) {
      <section class="card exp-col-12">
        <div class="card-head">
          <div class="card-title">Portfolio constituents</div>
        </div>
        <div style="overflow-x: auto">
          <table class="exp-table exp-table-sortable">
            <thead>
              <tr>
                <th
                  class="asset sortable"
                  [class.active]="sortKey === 'name'"
                  (click)="setSort('name')"
                >
                  <span>Asset</span>
                  @if (sortKey === "name") {
                    <span class="sort-ind">{{
                      sortDir === "asc" ? "▲" : "▼"
                    }}</span>
                  }
                </th>
                <th
                  class="asset sortable"
                  [class.active]="sortKey === 'type'"
                  (click)="setSort('type')"
                >
                  <span>Class</span>
                  @if (sortKey === "type") {
                    <span class="sort-ind">{{
                      sortDir === "asc" ? "▲" : "▼"
                    }}</span>
                  }
                </th>
                <th
                  class="sortable"
                  [class.active]="sortKey === 'weight'"
                  (click)="setSort('weight')"
                >
                  <span>Weight</span>
                  @if (sortKey === "weight") {
                    <span class="sort-ind">{{
                      sortDir === "asc" ? "▲" : "▼"
                    }}</span>
                  }
                </th>
                <th
                  class="sortable"
                  [class.active]="sortKey === 'cagr'"
                  (click)="setSort('cagr')"
                >
                  <span>CAGR</span>
                  @if (sortKey === "cagr") {
                    <span class="sort-ind">{{
                      sortDir === "asc" ? "▲" : "▼"
                    }}</span>
                  }
                </th>
                <th
                  class="sortable"
                  [class.active]="sortKey === 'volatility'"
                  (click)="setSort('volatility')"
                >
                  <span>Volatility</span>
                  @if (sortKey === "volatility") {
                    <span class="sort-ind">{{
                      sortDir === "asc" ? "▲" : "▼"
                    }}</span>
                  }
                </th>
                <th
                  class="sortable"
                  [class.active]="sortKey === 'riskContrib'"
                  (click)="setSort('riskContrib')"
                >
                  <span>Risk contrib.</span>
                  @if (sortKey === "riskContrib") {
                    <span class="sort-ind">{{
                      sortDir === "asc" ? "▲" : "▼"
                    }}</span>
                  }
                </th>
                <th
                  class="sortable"
                  [class.active]="sortKey === 'maxDrawdown'"
                  (click)="setSort('maxDrawdown')"
                >
                  <span>Max DD</span>
                  @if (sortKey === "maxDrawdown") {
                    <span class="sort-ind">{{
                      sortDir === "asc" ? "▲" : "▼"
                    }}</span>
                  }
                </th>
                <th
                  class="sortable"
                  [class.active]="sortKey === 'sharpe'"
                  (click)="setSort('sharpe')"
                >
                  <span>Sharpe</span>
                  @if (sortKey === "sharpe") {
                    <span class="sort-ind">{{
                      sortDir === "asc" ? "▲" : "▼"
                    }}</span>
                  }
                </th>
              </tr>
            </thead>
            <tbody>
              @for (c of constituents; track c) {
                <tr>
                  <td class="asset">
                    <span class="ticker">{{ c.ticker }}</span>
                    <span class="asset-link" (click)="onConstituentClick(c)">{{
                      c.name
                    }}</span>
                  </td>
                  <td
                    class="asset dim"
                    style="
                      text-transform: uppercase;
                      letter-spacing: 0.04em;
                      font-size: 11px;
                    "
                  >
                    {{ c.type }}
                  </td>
                  <td class="num">{{ fmtPct(c.weight, 2) }}</td>
                  <td
                    class="num"
                    [class.pos]="c.cagr > 0"
                    [class.neg]="c.cagr < 0"
                  >
                    {{ fmtPct(c.cagr, 2) }}
                  </td>
                  <td class="num">{{ fmtPct(c.volatility, 2) }}</td>
                  <td class="num">{{ fmtPct(c.riskContrib, 2) }}</td>
                  <td class="num neg">{{ fmtPct(c.maxDrawdown, 2) }}</td>
                  <td class="num">{{ fmtNum(c.sharpe) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>
    }
    <!-- Row 5: Risk contribution vs allocation + Marginal risk -->
    <section class="card exp-col-6">
      <div class="card-head">
        <div class="card-title">Risk contribution vs allocation</div>
      </div>
      @if (allocVsRisk.length) {
        <app-alloc-risk-bars
          [rows]="allocVsRisk"
          [format]="fmtPct.bind(this)"
        ></app-alloc-risk-bars>
      } @else {
        <div class="empty-cell">No risk contribution data available.</div>
      }
    </section>
    <section class="card exp-col-6">
      <div class="card-head"><div class="card-title">Marginal risk</div></div>
      @if (marginalRows.length) {
        <app-exp-marginal-risk-chart
          [rows]="marginalRows"
          [format]="fmtPct.bind(this)"
          [axisTitle]="'Risk'"
        ></app-exp-marginal-risk-chart>
      } @else {
        <div class="empty-cell">No marginal risk data available.</div>
      }
    </section>
  </div>
}

@if (isLoading) {
  <div class="bento">
    <div class="kpi exp-col-3">
      <div class="shimmer" style="height: 80px"></div>
    </div>
    <div class="kpi exp-col-3">
      <div class="shimmer" style="height: 80px"></div>
    </div>
    <div class="kpi exp-col-3">
      <div class="shimmer" style="height: 80px"></div>
    </div>
    <div class="kpi exp-col-3">
      <div class="shimmer" style="height: 80px"></div>
    </div>
    <div class="card exp-col-8">
      <div class="shimmer" style="height: 320px"></div>
    </div>
    <div class="card exp-col-4">
      <div class="shimmer" style="height: 320px"></div>
    </div>
    <div class="card exp-col-12">
      <div class="shimmer" style="height: 240px"></div>
    </div>
  </div>
}
